import { useState, type ReactNode } from "react";

import { useDeleteAccount, useRequestAccountDelete } from "../../api/queries";
import { useSignOut } from "../../auth/useSignOut";
import { Button } from "../../ui/Button";
import { Dialog } from "../../ui/Dialog";
import { TextField } from "../../ui/Field";
import { SettingsSection } from "../../ui/SettingsSection";
import { useToast } from "../../ui/toast-context";

/**
 * Self-deletion is two calls on purpose (`docs/spec/users-and-teams.md` §9):
 * `users.requestDelete` mails a code, and `users.delete` needs that code. The
 * friction is the feature — this is the one action in the product with no undo —
 * so the dialog says what survives, what does not, and asks for the code.
 */
export function DeleteAccountSection(): ReactNode {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [requested, setRequested] = useState(false);
  const request = useRequestAccountDelete();
  const remove = useDeleteAccount();
  const signOut = useSignOut();
  const toast = useToast();

  const close = (): void => {
    setOpen(false);
    setCode("");
    setRequested(false);
    request.reset();
    remove.reset();
  };

  return (
    <SettingsSection
      title="Delete account"
      description="Your documents and comments stay in the workspace, reattributed to an anonymous author. Everything personal is destroyed. There is no undo."
    >
      <Button
        variant="danger"
        onClick={() => {
          setOpen(true);
        }}
      >
        Delete my account…
      </Button>

      <Dialog
        open={open}
        onClose={close}
        title="Delete your account"
        description="This cannot be reversed."
        width="sm"
      >
        {requested ? (
          <>
            <p className="text-sm text-muted">
              We emailed you a confirmation code. Enter it to finish deleting your account.
            </p>
            <div className="mt-4">
              <TextField
                label="Confirmation code"
                value={code}
                onChange={setCode}
                {...(remove.isError ? { error: remove.error.message } : {})}
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={code.trim() === "" || remove.isPending}
                onClick={() => {
                  remove.mutate(
                    { code: code.trim() },
                    {
                      onSuccess: () => {
                        setOpen(false);
                        void signOut();
                      },
                    },
                  );
                }}
              >
                {remove.isPending ? "Deleting…" : "Delete my account"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-muted">
              Deleting your account destroys your name, email, avatar, preferences and API keys, and
              signs you out everywhere. Documents and comments you wrote stay, under an anonymous
              author.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={close}>
                Keep my account
              </Button>
              <Button
                variant="danger"
                disabled={request.isPending}
                onClick={() => {
                  request.mutate(undefined, {
                    onSuccess: () => {
                      setRequested(true);
                    },
                    onError: (error) => {
                      toast.show(error.message, "error");
                    },
                  });
                }}
              >
                {request.isPending ? "Sending…" : "Email me a confirmation code"}
              </Button>
            </div>
          </>
        )}
      </Dialog>
    </SettingsSection>
  );
}
